import { FC, useMemo, useState } from "react";
import { Box, Button, FormControlLabel, Switch, Typography } from "@mui/material";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	CarbonSnapFormData,
	TransportArc,
	TransportMode,
	TransportNode,
} from "../../../../../../datasources/carbonsnap/models";
import { ROUTE_NODE_LEADING_WIDTH, ROUTE_NODE_TRAILING_WIDTH } from "../RouteNodeInput";
import ArcDetailsDialog from "./ArcDetailsDialog";
import TransportModePicker from "./TransportModePicker";

interface Props {
	form: UseFormReturn<CarbonSnapFormData>;
	arcIndex: number;
	fromNodeIndex: number;
	toNodeIndex: number;
}

const ArcControls: FC<Props> = ({ form, arcIndex, fromNodeIndex, toNodeIndex }) => {
	const { t } = useTranslation("whatIf.carbonsnap");
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const arc = useWatch({ control: form.control, name: `arcs.${arcIndex}` }) as TransportArc | undefined;
	const nodes = useWatch({ control: form.control, name: "nodes" }) as TransportNode[] | undefined;

	const fromNode = nodes?.[fromNodeIndex];
	const toNode = nodes?.[toNodeIndex];

	const canEdit = useMemo(() => {
		return Boolean(fromNode?.address && toNode?.address && fromNode?.lat !== null && toNode?.lat !== null);
	}, [fromNode, toNode]);

	const handleModeSelection = (mode: TransportMode) => {
		form.setValue(`arcs.${arcIndex}.transportMode`, mode);
	};

	const handleNetworkToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		form.setValue(`arcs.${arcIndex}.networkEnabled`, event.target.checked);
	};

	return (
		<Box sx={{ px: 0.5, pb: 1.5, minWidth: 0 }}>
			<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 1, columnGap: 1, minWidth: 0 }}>
				{/* Keeps the row below aligned under the address input, under the drag handle + node icon above */}
				<Box sx={{ width: ROUTE_NODE_LEADING_WIDTH, flexShrink: 0 }} />

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						rowGap: 1,
						columnGap: 2,
						justifyContent: "space-between",
						flexGrow: 1,
						minWidth: 0,
					}}
				>
					<Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
						<TransportModePicker value={arc?.transportMode} onChange={handleModeSelection} />

						<Button
							variant="text"
							disabled={!canEdit}
							onClick={() => setIsDialogOpen(true)}
							sx={{
								minWidth: "auto",
								px: 1.5,
								fontWeight: 600,
							}}
						>
							{t("arc/controls/edit")}
						</Button>
					</Box>

					<FormControlLabel
						control={
							<Switch size="small" checked={arc?.networkEnabled ?? true} onChange={handleNetworkToggle} />
						}
						label={<Typography variant="body2">{t("arc/controls/network")}</Typography>}
					/>
				</Box>

				{/* Mirrors the delete icon button width in the row above */}
				<Box sx={{ width: ROUTE_NODE_TRAILING_WIDTH, flexShrink: 0 }} />
			</Box>

			<ArcDetailsDialog
				open={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
				form={form}
				arcIndex={arcIndex}
				arc={arc}
				fromNode={fromNode}
				toNode={toNode}
			/>
		</Box>
	);
};

export default ArcControls;
